import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import html2pdf from 'html2pdf.js';
import { apiFetch } from '../../api/client';
import { withUnidade } from '../../utils/storageByUnit';

const STATUS_CHIP = {
  AGENDADA: { color: 'info', label: 'Agendada' },
  ATIVA: { color: 'success', label: 'Ativa' },
  ENCERRADA: { color: 'default', label: 'Encerrada' },
  CANCELADA: { color: 'error', label: 'Cancelada' },
};

const emptyForm = {
  titularId: '',
  substitutoId: '',
  cargo: '',
  setor: '',
  motivo: 'FERIAS',
  observacao: '',
  dataInicio: '',
  dataFim: '',
};

function formatCpf(v) {
  const d = String(v || '').replace(/\D/g, '');
  if (d.length !== 11) return v || '________';
  return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

function gerarHtmlDocumento(doc) {
  const hl = (t) =>
    `<span style="background:#fff59d;padding:0 2px;font-weight:bold;">${t || '________'}</span>`;
  return `
  <div style="font-family: Arial, sans-serif; font-size: 14px; line-height: 1.5; text-align: justify; color: #000; width: 210mm; min-height: 297mm; margin: 0 auto; box-sizing: border-box; padding: 40px; background: #fff;">
    <h3 style="text-align:center;font-weight:bold;">ATO DE DESIGNAÇÃO – SUBSTITUIÇÃO DE COLABORADOR</h3>
    <h4 style="font-weight:bold;background:#f0f0f0;padding:5px;">DADOS DA SUBSTITUIÇÃO</h4>
    <table style="width:100%;border-collapse:collapse;margin-bottom:20px;">
      <tr><td style="padding:4px;"><strong>UNIDADE:</strong> ${doc.unidade || '________'}</td>
          <td style="padding:4px;"><strong>DATA DE EMISSÃO:</strong> ${doc.dataEmissao || '________'}</td></tr>
      <tr><td colspan="2" style="padding:4px;"><strong>TITULAR:</strong> ${doc.titularNome} — ${doc.titularCargo} — Setor ${doc.titularSetor}</td></tr>
      <tr><td colspan="2" style="padding:4px;"><strong>SUBSTITUTO:</strong> ${doc.substitutoNome}, CPF ${doc.substitutoCpf} — ${doc.substitutoCargo} — Setor ${doc.substitutoSetor}</td></tr>
      <tr><td style="padding:4px;"><strong>MOTIVO:</strong> ${doc.motivo}</td>
          <td style="padding:4px;"><strong>PERÍODO:</strong> ${doc.dataInicio} a ${doc.dataFim}</td></tr>
    </table>
    <p>O Diretor Executivo, no uso das atribuições, resolve designar o(a) colaborador(a) ${hl(doc.substitutoNome)}, inscrito(a) no CPF ${hl(doc.substitutoCpf)}, lotado(a) no(a) Setor ${hl(doc.substitutoSetor)}, para substituir o(a) colaborador(a) ${hl(doc.titularNome)}, ${hl(doc.titularCargo)}, durante o período de ${hl(doc.dataInicio)} a ${hl(doc.dataFim)}, em razão de ${hl(doc.motivo)}, assumindo, no período indicado, as atribuições e responsabilidades inerentes à função, bem como o acesso aos módulos e sistemas necessários ao desempenho dessas atribuições.</p>
    <div style="text-align:center;margin-top:48px;">
      <p>_________________________________________________</p>
      <p><strong>${doc.diretorNome || '________'}</strong></p>
      <p>Diretor Executivo</p>
    </div>
    <h4 style="text-align:center;font-weight:bold;margin-top:40px;">CIÊNCIA DOS COLABORADORES DESIGNADOS</h4>
    <p>Eu, ${hl(doc.substitutoNome)}, declaro-me ciente da designação ora atribuída.</p>
    <div style="text-align:center;margin-top:36px;"><p>_________________________________________________</p><p><strong>Assinatura Substituto</strong></p></div>
    <p style="margin-top:28px;">Eu, ${hl(doc.titularNome)}, declaro-me ciente da designação ora atribuída.</p>
    <div style="text-align:center;margin-top:36px;"><p>_________________________________________________</p><p><strong>Assinatura Titular</strong></p></div>
  </div>`;
}

export default function SubstitutosTab({
  unidadeSelecionada,
  usuarios = [],
  onNotify,
}) {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({ status: '', titularId: '', substitutoId: '' });
  const [modalOpen, setModalOpen] = useState(false);
  const [detalhe, setDetalhe] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  const ativos = useMemo(
    () => (usuarios || []).filter((u) => !u.inativo && !String(u.id).startsWith('mock')),
    [usuarios]
  );

  const fetchLista = useCallback(async () => {
    if (!unidadeSelecionada?.codigo) {
      setLista([]);
      return;
    }
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filtros.status) qs.set('status', filtros.status);
      if (filtros.titularId) qs.set('titularId', filtros.titularId);
      if (filtros.substitutoId) qs.set('substitutoId', filtros.substitutoId);
      const path = withUnidade(
        `/api/substituicoes${qs.toString() ? `?${qs}` : ''}`,
        unidadeSelecionada
      );
      const res = await apiFetch(path);
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Erro ao listar');
      setLista(await res.json());
    } catch (e) {
      setLista([]);
      onNotify?.(e.message || 'Erro ao carregar substituições', 'error');
    } finally {
      setLoading(false);
    }
  }, [unidadeSelecionada, filtros, onNotify]);

  useEffect(() => {
    fetchLista();
  }, [fetchLista]);

  const abrirNovo = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setWarning('');
    setModalOpen(true);
  };

  const abrirEditar = (row) => {
    setEditing(row);
    setForm({
      titularId: String(row.titularId),
      substitutoId: String(row.substitutoId),
      cargo: row.cargo || '',
      setor: row.setor || '',
      motivo: row.motivo || 'FERIAS',
      observacao: row.observacao || '',
      dataInicio: row.dataInicio || '',
      dataFim: row.dataFim || '',
    });
    setError('');
    setWarning('');
    setModalOpen(true);
  };

  const onTitularChange = (id) => {
    const u = ativos.find((x) => String(x.id) === String(id));
    setForm((f) => ({
      ...f,
      titularId: id,
      cargo: u?.cargo || u?.tipo || f.cargo,
      setor: u?.setor || f.setor,
    }));
  };

  const salvar = async () => {
    setSaving(true);
    setError('');
    setWarning('');
    try {
      const body = {
        titularId: Number(form.titularId),
        substitutoId: Number(form.substitutoId),
        cargo: form.cargo,
        setor: form.setor,
        motivo: form.motivo,
        observacao: form.observacao,
        dataInicio: form.dataInicio,
        dataFim: form.dataFim,
      };
      const url = editing
        ? withUnidade(`/api/substituicoes/${editing.id}`, unidadeSelecionada)
        : withUnidade('/api/substituicoes', unidadeSelecionada);
      const res = await apiFetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Erro ao salvar');
      if (data.warning) setWarning(data.warning);
      setModalOpen(false);
      onNotify?.(editing ? 'Substituição atualizada.' : 'Substituição criada.', 'success');
      fetchLista();
    } catch (e) {
      setError(e.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const acaoStatus = async (row, tipo) => {
    const ok = window.confirm(
      tipo === 'cancelar'
        ? 'Cancelar esta substituição?'
        : 'Encerrar antecipadamente esta substituição?'
    );
    if (!ok) return;
    try {
      const res = await apiFetch(
        withUnidade(`/api/substituicoes/${row.id}/${tipo}`, unidadeSelecionada),
        { method: 'POST' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Falha na operação');
      onNotify?.(tipo === 'cancelar' ? 'Substituição cancelada.' : 'Substituição encerrada.', 'success');
      fetchLista();
    } catch (e) {
      onNotify?.(e.message, 'error');
    }
  };

  const baixarPdf = async (row) => {
    try {
      const res = await apiFetch(
        withUnidade(`/api/substituicoes/${row.id}/documento`, unidadeSelecionada)
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro ao gerar documento');
      const wrap = document.createElement('div');
      wrap.innerHTML = gerarHtmlDocumento(data.documento);
      document.body.appendChild(wrap);
      await html2pdf()
        .set({
          margin: 10,
          filename: `substituicao_${row.id}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        })
        .from(wrap)
        .save();
      document.body.removeChild(wrap);
      onNotify?.('Documento gerado.', 'success');
    } catch (e) {
      onNotify?.(e.message, 'error');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="subtitle1" fontWeight={700}>
          Substitutos
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo}>
          Nova substituição
        </Button>
      </Box>

      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={12} sm={3}>
          <TextField
            select
            fullWidth
            size="small"
            label="Status"
            value={filtros.status}
            onChange={(e) => setFiltros((f) => ({ ...f, status: e.target.value }))}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.keys(STATUS_CHIP).map((s) => (
              <MenuItem key={s} value={s}>
                {STATUS_CHIP[s].label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            size="small"
            label="Titular"
            value={filtros.titularId}
            onChange={(e) => setFiltros((f) => ({ ...f, titularId: e.target.value }))}
          >
            <MenuItem value="">Todos</MenuItem>
            {ativos.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.nome}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            size="small"
            label="Substituto"
            value={filtros.substitutoId}
            onChange={(e) => setFiltros((f) => ({ ...f, substitutoId: e.target.value }))}
          >
            <MenuItem value="">Todos</MenuItem>
            {ativos.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.nome}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                <TableCell sx={{ fontWeight: 700 }}>Titular</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Cargo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Substituto</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Período</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Motivo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary" py={3}>
                      Nenhuma substituição cadastrada nesta unidade.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                lista.map((row) => {
                  const chip = STATUS_CHIP[row.status] || STATUS_CHIP.AGENDADA;
                  const editavel = row.status === 'AGENDADA' || row.status === 'ATIVA';
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.titular?.nome || '—'}</TableCell>
                      <TableCell>{row.cargo || '—'}</TableCell>
                      <TableCell>{row.substituto?.nome || '—'}</TableCell>
                      <TableCell>
                        {row.dataInicioBr} a {row.dataFimBr}
                      </TableCell>
                      <TableCell>{row.motivoLabel}</TableCell>
                      <TableCell>
                        <Chip size="small" color={chip.color} label={chip.label} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Detalhes">
                          <IconButton size="small" onClick={() => setDetalhe(row)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {editavel && (
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => abrirEditar(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {editavel && (
                          <Tooltip title="Encerrar">
                            <IconButton size="small" onClick={() => acaoStatus(row, 'encerrar')}>
                              <StopCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {editavel && (
                          <Tooltip title="Cancelar">
                            <IconButton size="small" color="error" onClick={() => acaoStatus(row, 'cancelar')}>
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Gerar PDF">
                          <IconButton size="small" color="primary" onClick={() => baixarPdf(row)}>
                            <PictureAsPdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar substituição' : 'Nova substituição'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {warning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {warning}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Titular"
                value={form.titularId}
                onChange={(e) => onTitularChange(e.target.value)}
              >
                {ativos.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.nome} — {u.cargo || u.tipo}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Substituto"
                value={form.substitutoId}
                onChange={(e) => setForm((f) => ({ ...f, substitutoId: e.target.value }))}
              >
                {ativos
                  .filter((u) => String(u.id) !== String(form.titularId))
                  .map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.nome} — {u.cargo || u.tipo}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cargo / função assumida"
                value={form.cargo}
                onChange={(e) => setForm((f) => ({ ...f, cargo: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Setor"
                value={form.setor}
                onChange={(e) => setForm((f) => ({ ...f, setor: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Motivo"
                value={form.motivo}
                onChange={(e) => setForm((f) => ({ ...f, motivo: e.target.value }))}
              >
                <MenuItem value="FERIAS">Férias</MenuItem>
                <MenuItem value="AFASTAMENTO">Afastamento</MenuItem>
                <MenuItem value="LICENCA">Licença</MenuItem>
                <MenuItem value="OUTRO">Outro</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                type="date"
                label="Início"
                InputLabelProps={{ shrink: true }}
                value={form.dataInicio}
                onChange={(e) => setForm((f) => ({ ...f, dataInicio: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                type="date"
                label="Fim"
                InputLabelProps={{ shrink: true }}
                value={form.dataFim}
                onChange={(e) => setForm((f) => ({ ...f, dataFim: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Observação (opcional)"
                value={form.observacao}
                onChange={(e) => setForm((f) => ({ ...f, observacao: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={salvar} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(detalhe)} onClose={() => setDetalhe(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhes da substituição</DialogTitle>
        <DialogContent>
          {detalhe && (
            <Box display="flex" flexDirection="column" gap={1} mt={1}>
              <Typography>
                <strong>Titular:</strong> {detalhe.titular?.nome}
              </Typography>
              <Typography>
                <strong>Substituto:</strong> {detalhe.substituto?.nome} — CPF{' '}
                {formatCpf(detalhe.substituto?.cpf)}
              </Typography>
              <Typography>
                <strong>Cargo:</strong> {detalhe.cargo} · <strong>Setor:</strong> {detalhe.setor}
              </Typography>
              <Typography>
                <strong>Período:</strong> {detalhe.dataInicioBr} a {detalhe.dataFimBr}
              </Typography>
              <Typography>
                <strong>Motivo:</strong> {detalhe.motivoLabel}
              </Typography>
              {detalhe.observacao && (
                <Typography>
                  <strong>Obs.:</strong> {detalhe.observacao}
                </Typography>
              )}
              <Chip
                size="small"
                sx={{ alignSelf: 'flex-start' }}
                color={(STATUS_CHIP[detalhe.status] || {}).color || 'default'}
                label={(STATUS_CHIP[detalhe.status] || {}).label || detalhe.status}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetalhe(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
